/* eslint-disable no-undef */
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faClock } from '@fortawesome/pro-regular-svg-icons/faClock';
import { faHomeLgAlt } from '@fortawesome/pro-regular-svg-icons/faHomeLgAlt';
import { faMapMarkerAlt } from '@fortawesome/pro-regular-svg-icons/faMapMarkerAlt';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import { Property } from '@propertypal/shared/src/types/property';
import getOpeningTimes from '@propertypal/shared/src/utils/agent/getOpeningTimes';
import getContact from '@propertypal/shared/src/utils/enquiries/getContact';
import getSellingAgent, { getAgentColours } from '@propertypal/shared/src/utils/property/getSellingAgent';
import Link from 'next/link';
import React, { CSSProperties, FunctionComponent, useState } from 'react';
import { useTheme } from 'styled-components';
import Button from '../buttons/Button';
import HorizontalDivider from '../divider/HorizontalDivider';
import Chevron from '../icons/Chevron';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import { Text, LinkText } from '../typography';
import {
  Title,
  AgentBox,
  AgentLogo,
  AgentText,
  LicenceWarning,
  AgentBoxHeader,
  AgentBoxContent,
  AgentExtraInfo,
  AgentListItem,
  IconBox,
  ShowMoreDetails,
  AgentButtons,
} from './PropertyAgentBox.style';
import PropertyAgentBoxFixed from './PropertyAgentBoxFixed';
import PropertyPhoneNumberButton from './PropertyPhoneNumberButton';

interface Props {
  containerStyle?: CSSProperties;
  property: Property;
  onCallClick: (location: string) => void;
  onShowNumberClick: (location: string) => void;
  onEnquiryClick: () => void;
  onRegisterInterestClick: () => void;
  onRequestValuationClick: () => void;
  registerInterest?: boolean;
  requestValuation?: boolean;
  leadId: number;
}

const PropertyAgentBox: FunctionComponent<Props> = (props) => {
  const { property } = props;
  const theme = useTheme();
  const sellingAgent = getSellingAgent(property);
  const {
    pdpBrandingBgColour,
    pdpBrandingBorderColour,
    pdpBrandingButtonBgColour,
    pdpBrandingButtonTextColour,
    pdpBrandingLinkColour,
  } = getAgentColours(property.enhancedBranding, sellingAgent);
  const [showMore, setShowMore] = useState(false);
  // const agentAward = sellingAgent?.propertypalAwards?.length ? sellingAgent?.propertypalAwards[0] : null;
  const timeLabels = getOpeningTimes(sellingAgent?.openingTimes || []);
  const contactInfo = getContact(property);
  const page = property.type === 'development' ? 'DDP' : 'PDP';
  const leadsLink = property.leadIds?.length
    ? `/my/enquiries?leadId=${property.leadIds[0]}`
    : props.leadId
    ? `/my/enquiries?leadId=${props.leadId}`
    : '';

  const enquireNowClick = (location: string) => {
    trackGaEvent('enquiry_click', { page, location });
    props.onEnquiryClick();
  };

  const registerInterestClick = (location: string) => {
    trackGaEvent('register_interest_click', { page, location });
    props.onRegisterInterestClick();
  };

  const requestValuationClick = () => {
    trackGaEvent('request_valuation_click', { page, location: 'Enquiry Box' });
    props.onRequestValuationClick();
  };

  return (
    <>
      <AgentBox className="pp-contact-agent" color={pdpBrandingBorderColour}>
        {sellingAgent && (
          <>
            <AgentBoxHeader color={pdpBrandingBgColour}>
              <Link href={sellingAgent.landingPageUrl} prefetch={false}>
                {sellingAgent.logo && sellingAgent.logo.standard && (
                  <AgentLogo data-testid="agent-logo" src={sellingAgent.logo.standard} alt="Agent Logo" />
                )}
              </Link>
            </AgentBoxHeader>

            <Title>Contact {sellingAgent.organisation}</Title>
          </>
        )}

        {!sellingAgent && <Title>Marketed by multiple agents</Title>}

        {sellingAgent && sellingAgent.psrLicenceNumber && property.countryCode === 'IRL' && (
          <AgentText>
            PSR Licence: {sellingAgent.psrLicenceNumber}
            {sellingAgent.psrLicenceNumber === 'NO LICENCE' && (
              <LicenceWarning href="/psr-licence-number" prefetch={false}>
                ?
              </LicenceWarning>
            )}
          </AgentText>
        )}

        <Text className="show-print" align="center" fontSize={18} mb={15}>
          {sellingAgent?.officePhone?.international}
        </Text>

        {!!property.leadIds?.length && (
          <Text
            align="center"
            color={theme.primary}
            fontSize={14}
            fontWeight="600"
            style={{ maxWidth: 240, margin: '0 auto' }}
          >
            <FontAwesomeIcon icon={faExclamationCircle} style={{ marginRight: 8 }} />
            You&apos;ve already made an enquiry about this property.{' '}
            <Link href={leadsLink} style={{ textDecoration: 'underline' }}>
              View <Chevron size={10} />
            </Link>
          </Text>
        )}

        <AgentBoxContent className="hide-print">
          <AgentButtons>
            {/* {sellingAgent?.enhancedBranding && agentAward && (
                <Link href={sellingAgent.landingPageUrl} prefetch={false}>
                  <AwardLogo src={agentAward.imageUrl} alt={agentAward.label} data-testid="award-logo" />
                </Link>
              )} */}

            {property.published && (
              <>
                {props.registerInterest && !leadsLink && (
                  <>
                    <Button
                      containerStyle={{ height: 50, marginBottom: 15, maxWidth: 400 }}
                      onClick={() => registerInterestClick('Enquiry Box')}
                      testID="registerInterestButton"
                      type="button"
                      className="pp-register-interest"
                      color={pdpBrandingButtonBgColour}
                      fontColor={pdpBrandingButtonTextColour}
                    >
                      Register Your Interest
                    </Button>

                    <HorizontalDivider color={theme.textDark} dividerColor={theme.backgroundLight} text="or" mb={15} />
                  </>
                )}

                {!!leadsLink && (
                  <Link href={leadsLink} style={{ width: '100%', maxWidth: 400, marginBottom: 15 }}>
                    <Button
                      containerStyle={{ height: 50 }}
                      type="button"
                      color={pdpBrandingButtonBgColour}
                      fontColor={pdpBrandingButtonTextColour}
                    >
                      Send another message
                    </Button>
                  </Link>
                )}

                {contactInfo.email && !leadsLink && (
                  <Button
                    containerStyle={{ height: 50, marginBottom: 15, maxWidth: 400 }}
                    onClick={() => enquireNowClick('Enquiry Box')}
                    testID="enquireNowButton"
                    type="button"
                    color={pdpBrandingButtonBgColour}
                    fontColor={pdpBrandingButtonTextColour}
                  >
                    Enquire Now
                  </Button>
                )}

                {contactInfo.phone && (
                  <PropertyPhoneNumberButton
                    {...props}
                    color={pdpBrandingButtonBgColour}
                    fontColor={pdpBrandingButtonTextColour}
                    containerStyle={{ height: 50, margin: '0 auto 15px', maxWidth: 400 }}
                    location="Enquiry Box"
                    useShortText={false}
                  />
                )}

                {props.requestValuation && (
                  <>
                    <HorizontalDivider color={theme.textDark} dividerColor={theme.backgroundLight} text="or" mb={15} />

                    <Button
                      containerStyle={{ height: 50, marginBottom: 15, maxWidth: 400 }}
                      onClick={requestValuationClick}
                      testID="requestValuationButton"
                      type="button"
                      color={pdpBrandingButtonBgColour}
                      fontColor={pdpBrandingButtonTextColour}
                    >
                      Request a Valuation
                    </Button>
                  </>
                )}
              </>
            )}
          </AgentButtons>

          {property.enhancedBranding && sellingAgent && (
            <AgentExtraInfo show={showMore}>
              {!!sellingAgent.displayAddress && (
                <AgentListItem>
                  <IconBox>
                    <FontAwesomeIcon icon={faMapMarkerAlt} color={theme.textDark} style={{ fontSize: 18 }} />
                  </IconBox>

                  <Text>
                    {sellingAgent.displayAddress}
                    <br />
                    {sellingAgent.landingPageUrl && (
                      <Link href={sellingAgent.landingPageUrl}>
                        <LinkText as="span" fontSize={14} color={pdpBrandingLinkColour}>
                          More information about us <Chevron size={11} />
                        </LinkText>
                      </Link>
                    )}
                  </Text>
                </AgentListItem>
              )}

              {!!timeLabels.length && (
                <AgentListItem>
                  <IconBox>
                    <FontAwesomeIcon icon={faClock} color={theme.textDark} style={{ fontSize: 18 }} />
                  </IconBox>

                  <div>
                    {timeLabels.map((timeLabel) => (
                      <Text key={timeLabel.firstDay}>
                        {timeLabel.firstDay}
                        {timeLabel.lastDay && ` to ${timeLabel.lastDay}`}: {timeLabel.openingTime}
                        {timeLabel.closingTime && `- ${timeLabel.closingTime}`}
                      </Text>
                    ))}
                  </div>
                </AgentListItem>
              )}

              {sellingAgent.searchUrl && (
                <AgentListItem>
                  <IconBox>
                    <FontAwesomeIcon icon={faHomeLgAlt} color={theme.textDark} style={{ fontSize: 18 }} />
                  </IconBox>

                  <Text>
                    <Link href={sellingAgent.searchUrl} prefetch={false}>
                      <LinkText as="span" fontSize={14} color={pdpBrandingLinkColour}>
                        View our similar properties <Chevron size={11} />
                      </LinkText>
                    </Link>
                  </Text>
                </AgentListItem>
              )}

              {/* {agentAward && sellingAgent.landingPageUrl && (
                  <AgentListItem>
                    <IconBox>
                      <FontAwesomeIcon icon={faTrophy} color={theme.textDark} style={{ fontSize: 18 }} />
                    </IconBox>

                    <Link href={sellingAgent.landingPageUrl} prefetch={false}>
                      <AwardLogo src={agentAward.imageUrl} alt={agentAward.label} data-testid="award-logo" />
                    </Link>
                  </AgentListItem>
                )} */}
            </AgentExtraInfo>
          )}
        </AgentBoxContent>

        {property.enhancedBranding && (
          <ShowMoreDetails
            color={pdpBrandingLinkColour}
            onClick={() => setShowMore(true)}
            show={!showMore}
            className="hide-print"
          >
            Show more details <FontAwesomeIcon icon={faChevronDown} color={pdpBrandingLinkColour} />
          </ShowMoreDetails>
        )}
      </AgentBox>

      <PropertyAgentBoxFixed
        requestValuationClick={requestValuationClick}
        registerInterestClick={registerInterestClick}
        enquireNowClick={enquireNowClick}
        contactInfo={contactInfo}
        leadsLink={leadsLink}
        sellingAgent={sellingAgent}
        {...props}
      />
    </>
  );
};

export default PropertyAgentBox;
